import PropTypes from 'prop-types';
import React, { useState } from 'react';
import styled from 'styled-components';
import { Radio } from 'components/FormFields';
import Checkbox from 'components/v3/Checkbox';
import { AGREE_LABEL_DEFAULT, OTHER_LABEL } from '../../shared/fieldTypes';
import { phone } from '../palette';
import {
  FillInput,
  FillSelect,
  Stack,
  controlA11y,
  uniqueOptions,
} from './styles';
import { inputPropTypes } from './TextInputs';

// Dropdown, radio and checkbox (multi-select, or the single consent card).
// With config.allow_other a dropdown or radio gets an extra "Other" choice
// that opens a text box; the typed text is the answer (the server's
// answers.js `choice`). OTHER_VALUE only marks the control while the box is
// empty and is never stored.

export const OTHER_VALUE = '__fb_other__';

const optionsOf = (field) =>
  uniqueOptions(field.config && field.config.options);

const allowsOther = (field) =>
  !!(field.config && field.config.allow_other === true);

// "Other" is on once picked, or when the stored answer isn't one of the
// options (a typed value coming back from a draft or an edit).
const useOther = (field, value, options) => {
  const [picked, setPicked] = useState(false);
  const text = typeof value === 'string' ? value : '';
  const typed = !!text && options.indexOf(text) === -1;
  return { on: allowsOther(field) && (picked || typed), text, setPicked };
};

const OtherBox = ({ field, inputId, describedBy, invalid, text, onChange }) => (
  <div style={{ marginTop: 8 }}>
    <FillInput
      id={`${inputId}-other`}
      aria-label={`${field.label || 'Answer'}: ${OTHER_LABEL}`}
      aria-describedby={describedBy}
      aria-invalid={invalid || undefined}
      value={text}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Type your answer"
      $invalid={invalid}
    />
  </div>
);
OtherBox.propTypes = {
  field: PropTypes.object.isRequired,
  inputId: PropTypes.string.isRequired,
  describedBy: PropTypes.string,
  invalid: PropTypes.bool,
  text: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};
OtherBox.defaultProps = { describedBy: undefined, invalid: false };

const withOther = (field, options) =>
  allowsOther(field)
    ? options.concat({ label: OTHER_LABEL, value: OTHER_VALUE })
    : options;

export const DropdownInput = (props) => {
  const { field, value, onChange, inputId, describedBy, invalid } = props;
  const options = optionsOf(field);
  const other = useOther(field, value, options);
  const selected = other.on
    ? OTHER_VALUE
    : typeof value === 'string'
    ? value
    : '';
  const pick = (next) => {
    if (next === OTHER_VALUE) {
      other.setPicked(true);
      onChange('');
      return;
    }
    other.setPicked(false);
    onChange(next);
  };
  return (
    <>
      <FillSelect
        {...controlA11y(props)}
        placeholder={field.placeholder || 'Select an option'}
        options={withOther(
          field,
          options.map((o) => ({ label: o, value: o }))
        )}
        value={selected}
        onChange={(e) => pick(e.target.value)}
        $empty={!selected}
        $invalid={invalid}
      />
      {other.on ? (
        <OtherBox
          field={field}
          inputId={inputId}
          describedBy={describedBy}
          invalid={invalid}
          text={other.text}
          onChange={onChange}
        />
      ) : null}
    </>
  );
};
DropdownInput.propTypes = inputPropTypes;

export const RadioInput = ({
  field,
  value,
  onChange,
  inputId,
  labelId,
  describedBy,
  invalid,
}) => {
  const options = optionsOf(field);
  const other = useOther(field, value, options);
  const selected = other.on
    ? OTHER_VALUE
    : typeof value === 'string'
    ? value
    : '';
  const pick = (next) => {
    if (next === OTHER_VALUE) {
      other.setPicked(true);
      onChange('');
      return;
    }
    other.setPicked(false);
    onChange(next);
  };
  return (
    <>
      <Radio
        role="radiogroup"
        aria-labelledby={labelId}
        aria-describedby={describedBy}
        name={inputId}
        options={withOther(
          field,
          options.map((o) => ({ label: o, value: o }))
        )}
        value={selected}
        onChange={(e) => pick(e.target.value)}
      />
      {other.on ? (
        <OtherBox
          field={field}
          inputId={inputId}
          describedBy={describedBy}
          invalid={invalid}
          text={other.text}
          onChange={onChange}
        />
      ) : null}
    </>
  );
};
RadioInput.propTypes = inputPropTypes;

// Multi-select: one boxed checkbox per option; the answer keeps option order.
export const CheckboxGroupInput = ({
  field,
  value,
  onChange,
  labelId,
  describedBy,
  invalid,
}) => {
  const options = optionsOf(field);
  const picked = Array.isArray(value) ? value : [];
  const toggle = (option, on) => {
    const next = new Set(picked);
    if (on) next.add(option);
    else next.delete(option);
    onChange(options.filter((o) => next.has(o)));
  };
  return (
    <Stack
      role="group"
      aria-labelledby={labelId}
      aria-describedby={describedBy}
    >
      {options.map((option) => (
        <Checkbox
          key={option}
          boxed
          label={option}
          checked={picked.includes(option)}
          error={invalid}
          onChange={(on) => toggle(option, on)}
        />
      ))}
    </Stack>
  );
};
CheckboxGroupInput.propTypes = inputPropTypes;

const ConsentCard = styled.div`
  border: 1px solid
    ${(p) => (p.$invalid ? phone(p.theme).error : phone(p.theme).border)};
  border-radius: 10px;
  overflow: hidden;
  background: ${(p) => phone(p.theme).surface};
`;

// Line breaks are kept; long text scrolls in its own 300px box (design
// .m-consent-body).
const ConsentBody = styled.div`
  max-height: 300px;
  overflow-y: auto;
  padding: 15px 16px;
  font-size: 13px;
  line-height: 1.55;
  white-space: pre-line;
  overflow-wrap: anywhere;
  color: ${(p) => phone(p.theme).text2};
`;

const ConsentAgree = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 14px 16px;
  border-top: 1px solid ${(p) => phone(p.theme).border2};
  font-weight: 600;
`;

const agreeLabel = (field) => {
  const text = field.config && field.config.agree_label;
  return typeof text === 'string' && text.trim()
    ? text.trim()
    : AGREE_LABEL_DEFAULT;
};

// An agreement (single checkbox): options[0] is the agreement text, above
// the checkbox text (config.agree_label, "I have read and agree" if blank).
export const ConsentInput = ({
  field,
  value,
  onChange,
  inputId,
  describedBy,
  invalid,
}) => {
  const options = (field.config && field.config.options) || [];
  const statement = typeof options[0] === 'string' ? options[0] : '';
  return (
    <ConsentCard $invalid={invalid}>
      {statement.trim() ? <ConsentBody>{statement}</ConsentBody> : null}
      <ConsentAgree>
        <Checkbox
          id={inputId}
          describedBy={describedBy}
          label={agreeLabel(field)}
          checked={value === true}
          error={invalid}
          onChange={(on) => onChange(on)}
        />
      </ConsentAgree>
    </ConsentCard>
  );
};
ConsentInput.propTypes = inputPropTypes;

export const CheckboxInput = (props) =>
  props.field.config && props.field.config.single ? (
    <ConsentInput {...props} />
  ) : (
    <CheckboxGroupInput {...props} />
  );
CheckboxInput.propTypes = inputPropTypes;
